import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu as MenuIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Logo } from '../shell/Logo';
import { ThemeSwitch } from '../shell/ThemeSwitch';
import { Dialog, IconButton, buttonClasses } from '../ui';

const NAV_LINKS = [
  { id: 'how', label: "So funktioniert's" },
  { id: 'pricing', label: 'Preise' },
  { id: 'faq', label: 'FAQ' },
];

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
}

/** Sticky landing header: logo, section anchors, theme switch, Anmelden/Zu deinen Suchen. */
export const SiteHeader: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleAnchorClick = (event: React.MouseEvent, id: string) => {
    event.preventDefault();
    scrollToId(id);
    setMenuOpen(false);
  };

  const authCta = isAuthenticated ? (
    <Link to="/dashboard" className={buttonClasses('secondary', 'sm')}>
      Zu deinen Suchen
    </Link>
  ) : (
    <Link to="/login" className={buttonClasses('secondary', 'sm')}>
      Anmelden
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-page/90 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-wide items-center justify-between gap-4 px-4 md:px-6">
        <Logo />

        <nav aria-label="Hauptnavigation" className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(event) => handleAnchorClick(event, link.id)}
              className="rounded-control px-3 py-2 text-sm font-medium text-ink hover:bg-sunken"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <ThemeSwitch size="sm" showLabel={false} />
          {authCta}
        </div>

        <IconButton
          label="Menü"
          icon={<MenuIcon className="h-5 w-5" />}
          className="md:hidden"
          onClick={() => setMenuOpen(true)}
        />
      </div>

      <Dialog open={menuOpen} onClose={() => setMenuOpen(false)} title="Menü">
        <nav aria-label="Hauptnavigation mobil" className="flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(event) => handleAnchorClick(event, link.id)}
              className="rounded-control px-3 py-2 text-base font-medium text-ink hover:bg-sunken"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="mt-4 border-t border-line pt-4">
          <ThemeSwitch />
        </div>
        <div className="mt-4" onClick={() => setMenuOpen(false)}>
          {authCta}
        </div>
      </Dialog>
    </header>
  );
};

export default SiteHeader;
